/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";

import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import PageHeader from "@src/components/smart/PageHeader";
import { ThemePalette } from "@src/components/Theme";
import {
  HUB_ID,
  visibleSections,
} from "@src/components/modules/NavigationModule/Navigation/sections";
import { getRecentSections } from "@src/components/modules/NavigationModule/Navigation/recentSections";
import { SWIFT_REFRESH_EVENT } from "@src/components/modules/TemplateModule/TopBar/refresh";
import projectStore from "@src/stores/ProjectStore";
import userStore from "@src/stores/UserStore";
import configLoader from "@src/utils/Config";

import HubSections from "./HubSections";

/*
 * "/" - Swift's Overview, the landing page every Tech9IQ module has (the Ops Console's Nutanix
 * hub, AWS Management's connection home): every section as a card, the ones used most recently
 * first. The management dashboard stays at /dashboard.
 */
const Footer = styled.p`
  margin: 40px 0 8px 0;
  text-align: center;
  font-size: 12px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
`;

type State = { tick: number };

@observer
class HubPage extends React.Component<Record<string, never>, State> {
  state: State = { tick: 0 };

  componentDidMount() {
    document.title = "SwiftIQ | Tech9IQ";
    window.addEventListener(SWIFT_REFRESH_EVENT, this.handleRefresh);
    projectStore.getProjects({ skipLog: true });
  }

  componentWillUnmount() {
    window.removeEventListener(SWIFT_REFRESH_EVENT, this.handleRefresh);
  }

  handleRefresh = () => {
    this.setState(s => ({ tick: s.tick + 1 }));
    projectStore.getProjects({ skipLog: true });
  };

  render() {
    const user = userStore.loggedUser;
    const isAdmin = Boolean(user && user.isAdmin);
    const disabled = configLoader.config ? configLoader.config.disabledPages : [];
    const sections = visibleSections(isAdmin, disabled);
    const recent = getRecentSections(user ? user.id : "");

    return (
      <MainTemplate
        navigationComponent={<Navigation currentPage={HUB_ID} />}
        headerComponent={
          <PageHeader
            title="SwiftIQ"
            pill="Connected"
            pillTone="success"
            subtitle="Every SwiftIQ section. The ones you use most come first."
          />
        }
        listComponent={
          <>
            <HubSections sections={sections} recent={recent} />
            <Footer>© {new Date().getFullYear()} Tech9IQ Cloud · SwiftIQ</Footer>
          </>
        }
      />
    );
  }
}

export default HubPage;
